import {useEffect, useState} from 'react';

import constants from 'client/constants';

import {
  useCheckUsernameAvailabilityMutation,
  useSuggestUsernamesMutation
} from '../signUp.queries.generated';

const DEBOUNCE_MS = 400;

interface UsernameAvailability {
  available: boolean;
  reason?: string | null;
}

function useDebounced<T>(value: T, delay = DEBOUNCE_MS): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function useUsernameSuggestions({
  firstName,
  lastName,
  username
}: {
  firstName: string;
  lastName: string;
  username: string;
}) {
  const trimmedFirstName = firstName.trim();
  const trimmedLastName = lastName.trim();
  const trimmedUsername = username.trim();

  const [suggestionsFor, setSuggestionsFor] = useState<{
    firstName: string;
    lastName: string;
    result: string[];
  } | null>(null);
  const [availabilityFor, setAvailabilityFor] = useState<{
    username: string;
    result: UsernameAvailability;
  } | null>(null);

  const [suggestUsernames] = useSuggestUsernamesMutation();
  const [checkUsernameAvailability, {loading: isCheckingAvailability}] =
    useCheckUsernameAvailabilityMutation();

  const debouncedFirstName = useDebounced(trimmedFirstName);
  const debouncedLastName = useDebounced(trimmedLastName);
  const debouncedUsername = useDebounced(trimmedUsername);

  // Fetch suggestions when both debounced names settle and are non-empty.
  useEffect(() => {
    if (!debouncedFirstName || !debouncedLastName) return undefined;
    let cancelled = false;
    suggestUsernames({
      variables: {input: {firstName: debouncedFirstName, lastName: debouncedLastName}}
    }).then((response) => {
      if (cancelled) return;
      setSuggestionsFor({
        firstName: debouncedFirstName,
        lastName: debouncedLastName,
        result: response.data?.suggestUsernames ?? []
      });
    });
    return () => {
      cancelled = true;
    };
  }, [debouncedFirstName, debouncedLastName, suggestUsernames]);

  // Check availability for the current username after debounce.
  useEffect(() => {
    if (!debouncedUsername) return undefined;
    let cancelled = false;
    checkUsernameAvailability({variables: {input: {username: debouncedUsername}}}).then(
      (response) => {
        if (cancelled) return;
        const data = response.data?.checkUsernameAvailability;
        if (data) setAvailabilityFor({username: debouncedUsername, result: data});
      }
    );
    return () => {
      cancelled = true;
    };
  }, [debouncedUsername, checkUsernameAvailability]);

  // BE returns 9 suggestions — 3 per generator pattern, in order. Take one
  // from each pattern. The name-match check drops stale results while a new
  // fetch is in flight after the user edited a name.
  const matchesCurrentNames =
    suggestionsFor &&
    suggestionsFor.firstName === trimmedFirstName &&
    suggestionsFor.lastName === trimmedLastName;
  const suggestions = matchesCurrentNames
    ? [0, 3, 6].map((i) => suggestionsFor.result[i]).filter((s): s is string => !!s)
    : [];

  // Only expose availability when it matches what's currently typed — drops
  // stale responses from fields the user has already edited past.
  const availability =
    availabilityFor && availabilityFor.username === trimmedUsername ? availabilityFor.result : null;

  // Format check: matches the legacy regex (4–16 alphanumeric).
  const isFormatValid = constants.REGEX_COLLECTION.USERNAME.test(trimmedUsername);
  const isUsernameTaken = availability?.available === false;

  const formatError =
    trimmedUsername && !isFormatValid ? 'A username must be 4-16 alphanumeric characters' : null;
  const takenError =
    isFormatValid && isUsernameTaken
      ? availability?.reason || 'This username is already in use.'
      : null;
  const validationError = formatError || takenError;

  return {
    suggestions,
    availability,
    isCheckingAvailability,
    isFormatValid,
    isUsernameTaken,
    validationError
  };
}
